/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import { CentralLoadingIndicator, PetPlanCard, UpcomingPlanCard } from "../../../components";
import { PLAN_BILL_CYCLE_ID } from "../../../config/params";
import React, { useEffect, useState } from "react";
import { hasEndDateElapsed, isDateWithinFiveDays, isWithinPast30Days } from "../../../utils/unix-timestamp";
import { useAllServiceStatusStore, useCombinedAlertsDataStore } from "../../../hooks";

import { ClockIcon } from "../../../components/Icons";
import EnrollPlanCard from "../../../components/EnrollPlanCard/EnrollPlanCard";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import PaymentHistoryIcon from "../../../components/Icons/PaymentHistoryIcon";
import { PaymentMethodProvider } from "../../../services/renewal-preference/renewal-preference.interface";
import UserBillingIcon from "../../../components/Icons/UserBillingIcon";
import UserProfileMenu from "../../../components/UserProfileMenu/UserProfileMenu";
import { getPetOverdueStatus } from "../../../utils/plan-overdue-status";
import { useGetAutoRenewalUserPreferences } from "../../../services/renewal-preference/renewal-preference.service";
import { useHospitalOpenCheck } from "../../../hooks/useHospitalOpenCheck";
import { usePetList } from "../../../services/pet-list/pet-list.service";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUserDetail } from "../../../services/user-detail/user-detail.service";
import useWellnessPlansByUser from "../../../services/wellness-plans-by-user/wellness-plan-by-user.service";
import { userActivityLogger } from "../../../utils/mixpanel";

const GvPaymentLandingContainer = () => {
  const { user, loading } = useUserDetail();
  const { petsActive } = usePetList();
  const { petList }= usePetStore();
  const {
    shouldShowWellnessUpSell,
    scratchpayRenewalPending,
  } = useCombinedAlertsDataStore();
  const { getWellnessPlansByUser, data, loading: wellnessPlanDataLoading, recentlyCancelWellnessPlanByUser } = useWellnessPlansByUser();
  const { isHospitalOpened } = useHospitalOpenCheck();
  const { isWellnessServiceAvailable }=useAllServiceStatusStore();
  const { preferences: upcomingPlans, loading: upcomingPlansLoading } = useGetAutoRenewalUserPreferences();

  const upcomingPlansFiltered = upcomingPlans?.filter((p) => p.renewalStatus?.toLowerCase() === "pending");

  useEffect(() => {
    getWellnessPlansByUser();
    userActivityLogger(MIXPANEL_EVENTS.VISIT_PLAN, {});
  }, []);


  const renderRenewalCard = () => {
    if (isWellnessServiceAvailable && shouldShowWellnessUpSell) {
      return <EnrollPlanCard />;
    }

    return null;
  };

  if ((loading && !petsActive) || wellnessPlanDataLoading) {
    return <CentralLoadingIndicator />;
  }

  return (
    <div className="flex justify-center cursor-default">
      <div className="w-full max-w-[528px] flex flex-col items-center gap-2">
        {(data && data.length > 0) || recentlyCancelWellnessPlanByUser?
          <>
            <div className="w-full flex justify-between">
              <div className="text-sm font-semibold my-1">Your Current Plan</div>
              {data && data.length > 0 ?
                <div className="text-gray-1 text-sm font-semibold">Manage Plan</div> :
                null
              }
            </div>
          </>: null}
        {user &&
          <div className="w-full flex flex-col justify-between gap-5">
            {
              data && data.length > 0 ?
                <div className="flex flex-col gap-2">
                  {renderRenewalCard()}
                  {data.map((petData, index) => {
                    const upcomingPlan = upcomingPlansFiltered?.find((pref) => pref.currentSubscriptionId === petData.subscriptionId);
                    const hasUpcomingRenewal = !!upcomingPlan;

                    return <PetPlanCard
                      key={index}
                      billCycle={petData.planId === PLAN_BILL_CYCLE_ID.MONTHLY ? "perMonth" : "perYear"}
                      hasWellnessPlan={Boolean(petData.wellnessPlan && petData.wellnessPlanDetails?.wellnessId)}
                      activePetDetails={petData.pet}
                      subscriptionFee={petData.wellnessPlan?.price}
                      nextBillingDate={petData.wellnessPlanDetails?.nextBillingDate}
                      planRenewalDate={petData.wellnessPlanDetails?.nextRenewal}
                      cardDetails={petData.wellnessPlanDetails?.cardDetails}
                      planEndDate={petData.wellnessPlanDetails?.endDate}
                      isPlanEndingInFiveDays={isDateWithinFiveDays(petData.wellnessPlanDetails?.endDate)}
                      isPausedRenewUnsuccessful={hasEndDateElapsed(petData.wellnessPlanDetails?.endDate || "")}
                      isCancelledRenewUnsuccessful={isWithinPast30Days(petData.wellnessPlanDetails?.startDate || "") && petData.wellnessPlanDetails?.renewalStatus}
                      planOverdueStatus={getPetOverdueStatus(
                        petData.wellnessPlanPaymentStatus,
                        petData.isScratchpay,
                        petData.isOverdue,
                      )}
                      planName = {petData.planName}
                      hasUpcomingRenewal={hasUpcomingRenewal}
                      renewalStartDate={upcomingPlan?.newPlanStartDate}
                      isScratchpay={petData.isScratchpay}
                    />;
                  },
                  )}
                </div> :
                isWellnessServiceAvailable && shouldShowWellnessUpSell ?
                  <EnrollPlanCard /> :
                  null

            }
            {upcomingPlansFiltered && upcomingPlansFiltered.length > 0 && (
              <>
                <div className="w-full flex justify-between">
                  <div className="text-sm font-semibold my-1">Upcoming Plan</div>
                </div>
                <div className="flex flex-col gap-2">
                  {upcomingPlansFiltered
                    .map((preference, index) => {
                      // Get pet details from preference
                      const petDetails = petList?.find((pet) => pet.id === preference.animalId);
                      const planStartDate = preference.newPlanStartDate;
                      const billCycle = (preference.newPlanType?.toLowerCase() === "monthly") ? "perMonth" : "perYear";
                      const subscriptionFee = preference.newPlanSubscriptionFee?.toString() || "";

                      if (!petDetails || !planStartDate) return null;
                      // If the plan is scratchpay and the renewal is not pending, don't show the card
                      if (preference.paymentMethodProvider === PaymentMethodProvider.SCRATCH_PAY &&
                        !scratchpayRenewalPending.find((renewal) => renewal.petId === petDetails.id)
                      ) {
                        return null;
                      }

                      return (
                        <UpcomingPlanCard
                          key={index}
                          petName={petDetails.name}
                          petImage={petDetails.image ?? undefined}
                          planName={preference.newPlanName}
                          planStartDate={planStartDate}
                          subscriptionFee={subscriptionFee}
                          billCycle={billCycle}
                          currentSubscriptionId={preference.currentSubscriptionId}
                          petId={petDetails.id}
                          paymentMethodProvider={preference.paymentMethodProvider}
                          planType={preference.newPlanType}
                          scratchpayPlanId={preference.scratchpayPlanId}
                        />
                      );
                    })}
                </div>
              </>
            )}
            <div className="flex flex-col gap-2 mb-2">
              <UserProfileMenu
                icon={<ClockIcon />}
                text="Past Plans"
                navigateTo={PAGES_PATH.PAST_PLANS}
              />
              <UserProfileMenu
                icon={<UserBillingIcon />}
                text="Payment Method"
                navigateTo={PAGES_PATH.BILLING_METHOD}
              />
              <UserProfileMenu
                icon={<PaymentHistoryIcon />}
                text="Payment History"
                navigateTo={PAGES_PATH.BILLING_HISTORY}
              />
            </div>
          </div>}
      </div>
    </div>
  );
};

export default GvPaymentLandingContainer;
